import { DeliveryMan, Zone } from "@apis/index";
import Header from "@commons/header";
import {
  StyleCoverWrapper,
  StyleImageContainer,
  StyleProfileWrapper,
} from "@commons/store-setup/form/style";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import DateTimePicker from "@components/fields/datetime";
import DropDown from "@components/fields/dropdown";
import Row from "@components/row";
import Typography from "@components/typography";
import ButtonWrapper from "@components/wrapper/button-wrapper";

import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useDebounce from "@hooks/useDebounce";
import styled from "@emotion/styled";
import * as Yup from "yup";
import useAddressess from "@hooks/useAddressess";
import { vehicleBrand } from "../constant";

// Phone number validation rules by country code
const getPhoneValidationRules = (countryCode: number) => {
  switch (countryCode) {
    case 977: // Nepal
      return {
        length: 10,
        validPrefixes: ["98", "97", "96", "95"],
        message:
          "Nepal mobile numbers must be 10 digits and start with 98, 97, 96, or 95",
      };
    case 91: // India
      return {
        length: 10,
        validPrefixes: ["6", "7", "8", "9"],
        message:
          "India mobile numbers must be 10 digits and start with 6, 7, 8, or 9",
      };
    case 1: // USA/Canada
      return {
        length: 10,
        validPrefixes: null, // No specific prefix requirement
        message: "Phone number must be 10 digits",
      };
    default:
      return {
        length: 10,
        validPrefixes: null,
        message: "Phone number must be at least 10 digits",
      };
  }
};

// Validate phone number format based on country code
const validatePhoneFormat = (
  phoneNumber: string,
  countryCode: number,
): { isValid: boolean; message?: string } => {
  if (!phoneNumber) {
    return { isValid: false, message: "Phone number is required" };
  }

  // Extract digits only (remove country code and formatting)
  const digits = phoneNumber.replace(/\D/g, "");

  // Remove country code if present
  const countryCodeStr = countryCode.toString();
  let phoneDigits = digits;
  if (digits.startsWith(countryCodeStr)) {
    phoneDigits = digits.substring(countryCodeStr.length);
  }

  const rules = getPhoneValidationRules(countryCode);

  // Check length
  if (phoneDigits.length !== rules.length) {
    return {
      isValid: false,
      message: `Phone number must be exactly ${rules.length} digits`,
    };
  }

  // Check prefix if required
  if (rules.validPrefixes && rules.validPrefixes.length > 0) {
    const isValidPrefix = rules.validPrefixes.some((prefix) =>
      phoneDigits.startsWith(prefix),
    );
    if (!isValidPrefix) {
      return {
        isValid: false,
        message: rules.message,
      };
    }
  }

  return { isValid: true };
};

const ValidationMessage = styled.div<{ isValid?: boolean }>`
  font-size: 12px;
  margin-top: 4px;
  color: ${(props) => (props.isValid ? "#10b981" : "#ef4444")};
`;

const EmailSuggestions = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: #f3f4f6;
  border-radius: 6px;
  font-size: 12px;
`;

const SuggestionItem = styled.div`
  padding: 4px 0;
  cursor: pointer;
  color: #3b82f6;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const AddDeliveryMan = () => {
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [isOtherBrand, setIsOtherBrand] = useState<boolean>(false);
  const {
    provinces,
    districts,
    municipals,
    getProvinces,
    getDistrictsByProvince,
    getMunicipalitiesByDistrict,
  } = useAddressess({
    provinceName: selectedProvince,
    districtName: selectedDistrict,
  });
  const { data: zones } = useSelector((state: RootState) => state.zones);
  const fetchZones = useCallback(async () => {
    await Zone.list({ state: "zones" })
      .then(() => {})
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetchZones();
  }, []);

  const { id: editId } = useParams<{ id?: string }>();
  const { state: locationState } = useLocation();
  const isEditMode = !!editId;

  const [initialValues, setInitialState] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",

    zone: "",

    city: "",
    state: "",

    identityType: "",
    identityNumber: "",
    front: null,
    back: null,

    deliveryType: "",
    vehicleType: "",
    vehicleImage: null,

    frontBB: null,
    backBB: null,
    number: "", // Driving license number
    validity: "", // Driving license validity
    licenseFront: null,
    insuranceImg: null,
    cover: null,
    profile: null,
    province: "",
    district: "",
    municipality: "",
    vehicleBrand: "",
    vehicleName: "",
    vehicleNumber: "",
  });

  // Image field keys that map form fields to backend data keys
  const imageFieldMap: Record<string, string> = {
    front: "frontImg",
    back: "backImg",
    vehicleImage: "vehicleImg",
    frontBB: "bluebookFrontImg",
    backBB: "bluebookBackImg",
    licenseFront: "licenseImg",
    insuranceImg: "insuranceImg",
    cover: "coverImg",
    profile: "avatar",
  };

  // Pre-fill form when editing
  useEffect(() => {
    if (isEditMode && locationState) {
      const s = locationState as any;
      setInitialState({
        name: s.name || "",
        phone: s.phoneNo || "",
        email: s.email || "",
        password: "",
        zone: s.zone?.id || s.zoneId || "",
        city: s.city || "",
        state: s.state || "",
        identityType: s.identityType || "",
        identityNumber: s.identityNo || "",
        front: s.frontImg || null,
        back: s.backImg || null,
        deliveryType: s.type || "",
        vehicleType: s.vehicleType || "",
        vehicleImage: s.vehicleImg || null,
        frontBB: s.bluebookFrontImg || null,
        backBB: s.bluebookBackImg || null,
        number: s.licenseNo || "",
        validity: s.licenseValidity || "",
        licenseFront: s.licenseImg || null,
        insuranceImg: s.insuranceImg || null,
        cover: s.coverImg || null,
        profile: s.avatar || null,
        province: s.province || "",
        district: s.district || "",
        municipality: s.municipality || "",
        vehicleBrand: s.vehicleBrand || "",
        vehicleName: s.vehicleName || "",
        vehicleNumber: s.vehicleNumber || "",
      });
    }
  }, [isEditMode, locationState]);

  const navigate = useNavigate();

  // Validation state
  const [emailValidation, setEmailValidation] = useState<{
    message: string;
    isValid: boolean;
    suggestions?: string[];
  } | null>(null);
  const [phoneValidation, setPhoneValidation] = useState<{
    message: string;
    isValid: boolean;
  } | null>(null);

  const [isValidating, setIsValidating] = useState(false);

  // Track current form values for validation
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const [currentPhone, setCurrentPhone] = useState<string>("");
  const [currentPhoneValue, setCurrentPhoneValue] = useState<string>("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    callingCode: number;
    code: string;
  }>({ callingCode: 977, code: "NP" }); // Default to Nepal

  // Debounced values
  const debouncedEmail = useDebounce(currentEmail, 800);
  const debouncedPhone = useDebounce(currentPhone, 800);

  // Validation function
  const validateEmailAndPhone = useCallback(
    async (email?: string, phone?: string) => {
      if (!email && !phone) {
        setEmailValidation(null);
        setPhoneValidation(null);
        return;
      }

      setIsValidating(true);
      try {
        const params: any = {};
        if (email) params.email = email;
        if (phone) params.phoneNo = phone;

        const response = await DeliveryMan.checkEmailAndPhone({ params });

        if (response?.email) {
          setEmailValidation({
            message: response.email.message,
            isValid: !response.email.exists,
            suggestions: response.email.suggestions || [],
          });
        }

        if (response?.phone) {
          setPhoneValidation({
            message: response.phone.message,
            isValid: !response.phone.exists,
          });
        }
      } catch (err: any) {
        console.error("Validation error:", err);
        if (email) {
          setEmailValidation({
            message: "Error checking email availability",
            isValid: false,
          });
        }
        if (phone) {
          setPhoneValidation({
            message: "Error checking phone availability",
            isValid: false,
          });
        }
      } finally {
        setIsValidating(false);
      }
    },
    [],
  );

  // Validate on debounced values change
  useEffect(() => {
    const emailToCheck = debouncedEmail?.trim() || undefined;
    // Don't strip +, only strip spaces/dashes etc if needed.
    // Keeping + ensures we match numbers stored with + in DB.
    const rawPhone = debouncedPhone
      ? debouncedPhone.replace(/[^0-9+]/g, "")
      : undefined;
    // Ensure phone has + prefix and country code
    let phoneToCheck = rawPhone;
    if (rawPhone) {
      if (!rawPhone.startsWith("+")) {
        const callingCodeStr = selectedCountryCode.callingCode.toString();
        if (!rawPhone.startsWith(callingCodeStr)) {
          phoneToCheck = `+${callingCodeStr}${rawPhone}`;
        } else {
          phoneToCheck = `+${rawPhone}`;
        }
      }
    }

    if (emailToCheck || phoneToCheck) {
      const shouldValidateEmail =
        emailToCheck && emailToCheck.includes("@") && emailToCheck.length > 3;

      let shouldValidatePhone = false;
      // Get pure digits for length check
      const digitsOnly = phoneToCheck ? phoneToCheck.replace(/\D/g, "") : "";

      if (phoneToCheck && currentPhoneValue) {
        const formatCheck = validatePhoneFormat(
          currentPhoneValue,
          selectedCountryCode.callingCode,
        );
        shouldValidatePhone = formatCheck.isValid && digitsOnly.length >= 10;

        if (digitsOnly.length >= 10 && !formatCheck.isValid) {
          setPhoneValidation({
            message: formatCheck.message || "Invalid phone number format",
            isValid: false,
          });
        }
      } else if (phoneToCheck && digitsOnly.length >= 10) {
        shouldValidatePhone = true;
      }

      if (shouldValidateEmail || shouldValidatePhone) {
        validateEmailAndPhone(
          shouldValidateEmail ? emailToCheck : undefined,
          shouldValidatePhone ? phoneToCheck : undefined,
        );
      } else if (emailToCheck || phoneToCheck) {
        if (!shouldValidateEmail && emailToCheck) {
          setEmailValidation(null);
        }
        if (!shouldValidatePhone && phoneToCheck && digitsOnly.length < 10) {
          setPhoneValidation(null);
        }
      }
    } else {
      setEmailValidation(null);
      setPhoneValidation(null);
    }
  }, [
    debouncedEmail,
    debouncedPhone,
    validateEmailAndPhone,
    currentPhoneValue,
    selectedCountryCode.callingCode,
  ]);

  // Yup validation schema
  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string().required("Full Name is required"),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required")
        .test("email-availability", "Email already exists", async (value) => {
          if (!value) return true;
          if (emailValidation && !emailValidation.isValid) {
            return false;
          }
          return true;
        }),
      phone: Yup.string()
        .required("Mobile number is required")
        .test(
          "phone-availability",
          "Phone number already exists",
          async (value) => {
            if (!value) return true;
            const formatCheck = validatePhoneFormat(
              value,
              selectedCountryCode.callingCode,
            );
            if (!formatCheck.isValid) {
              return true; // Let validation message handle format error if we want, or fail here.
              // Actually we set proper error in the format check below
            }
            if (phoneValidation && !phoneValidation.isValid) {
              return false;
            }
            return true;
          },
        ),
      password: isEditMode
        ? Yup.string()
        : Yup.string().required("Password is required"),
      zone: isEditMode
        ? Yup.string()
        : Yup.string().required("Zone is required"),
      city: isEditMode
        ? Yup.string()
        : Yup.string().required("City is required"),
      state: isEditMode
        ? Yup.string()
        : Yup.string().required("State is required"),
      identityType: isEditMode
        ? Yup.string()
        : Yup.string().required("Identity Type is required"),
      identityNumber: isEditMode
        ? Yup.string()
        : Yup.string().required("Identity Number is required"),
      deliveryType: isEditMode
        ? Yup.string()
        : Yup.string().required("Delivery Type is required"),
      vehicleType: isEditMode
        ? Yup.string()
        : Yup.string().required("Vehicle Type is required"),
    });
  }, [
    emailValidation,
    phoneValidation,
    selectedCountryCode.callingCode,
    isEditMode,
  ]);
  // List of image form field names for checking
  const imageFields = Object.keys(imageFieldMap);

  const handleSubmit = async (values) => {
    const formData = new FormData();

    const fieldsMap = {
      name: "name",
      phone: "phoneNo",
      email: "email",
      password: "password",
      zone: "zoneId",
      city: "city",
      state: "state",
      identityType: "identityType",
      identityNumber: "identityNo",
      validity: "licenseValidity",
      number: "licenseNo",
      vehicleType: "vehicleType",
      deliveryType: "type",
      front: "frontImg",
      back: "backImg",
      vehicleImage: "vehicleImg",
      frontBB: "bluebookFrontImg",
      backBB: "bluebookBackImg",
      profile: "avatar",
      licenseFront: "licenseImg",
      insuranceImg: "insuranceImg",
      province: "province",
      district: "district",
      municipality: "municipality",
      vehicleBrand: "vehicleBrand",
      vehicleName: "vehicleName",
      vehicleNumber: "vehicleNumber",
    };

    Object.entries(fieldsMap).forEach(([field, key]) => {
      let value = values[field];

      // Skip empty password in edit mode
      if (field === "password" && isEditMode && !value) return;

      // Use actual validity value, only default if creating and empty
      if (field === "validity" && !isEditMode && !value) {
        value = "2026-01-21";
      }

      if (value !== null && value !== undefined && value !== "") {
        if (value?.rawFile) {
          // New file uploaded
          formData.append(key, value.rawFile);
        } else if (imageFields.includes(field) && typeof value === "string") {
          // Unchanged existing image path — skip it, don't send string to file field
          return;
        } else {
          formData.append(key, value);
        }
      }
    });

    if (isEditMode && editId) {
      await DeliveryMan.update({
        state: "deliveries",
        id: editId,
        data: formData,
      })
        .then(() => {
          navigate("/admin/users/delivery-man/delivery-man-lists");
        })
        .catch(() => {});
    } else {
      await DeliveryMan.create({ state: "deliveries", data: formData })
        .then(() => {
          navigate("/admin/users/delivery-man/delivery-man-lists");
        })
        .catch(() => {});
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ setFieldValue, values, errors, touched }) => (
        <Form>
          <Column>
            <Header
              title={isEditMode ? "Update Deliveryman" : "Deliveryman Setup"}
            />
            <Typography weight={600} size={18}>
              Delivery Information
            </Typography>
            <StyleImageContainer>
              <StyleCoverWrapper>
                <Fields.Upload
                  name="cover"
                  label="cover photo"
                  disabled={true}
                />
              </StyleCoverWrapper>
              <StyleProfileWrapper>
                <Fields.Upload name="profile" variant="profile" size={120} />
              </StyleProfileWrapper>
            </StyleImageContainer>
            <Row>
              <Fields.Input
                name="name"
                placeholder="Enter Full Name"
                label="Full Name"
              />
              <div id="phone-field-wrapper" style={{ width: "100%" }}>
                <Fields.PhoneInput
                  name="phone"
                  label="Mobile Number"
                  placeholder="Enter Mobile Number"
                  onChange={(value) => {
                    setFieldValue("phone", value);
                    // Keep + for syncing with DB storage
                    const phoneToDebounce = value
                      ? value.replace(/[^0-9+]/g, "")
                      : "";
                    setCurrentPhone(phoneToDebounce);
                    setCurrentPhoneValue(value || "");
                    setPhoneValidation(null);
                  }}
                  onChangeCode={(codes) => {
                    setSelectedCountryCode(codes);
                    setPhoneValidation(null);
                  }}
                />
                {phoneValidation && (
                  <ValidationMessage
                    id="phone-validation-message"
                    isValid={phoneValidation.isValid}
                  >
                    {phoneValidation.message}
                  </ValidationMessage>
                )}
                {isValidating && !phoneValidation && (
                  <ValidationMessage id="phone-validation-loading">
                    Checking availability...
                  </ValidationMessage>
                )}
                {errors.phone && touched.phone && !phoneValidation && (
                  <ValidationMessage role="alert">
                    {errors.phone as string}
                  </ValidationMessage>
                )}
              </div>
            </Row>
            <Row>
              <div id="email-field-wrapper" style={{ width: "100%" }}>
                <Fields.Input
                  name="email"
                  placeholder="Enter Email"
                  label="Email"
                  onChange={(value) => {
                    setFieldValue("email", value);
                    setCurrentEmail(value || "");
                    setEmailValidation(null);
                  }}
                />
                {emailValidation && (
                  <>
                    <ValidationMessage
                      id="email-validation-message"
                      isValid={emailValidation.isValid}
                    >
                      {emailValidation.message}
                    </ValidationMessage>
                    {emailValidation.suggestions &&
                      emailValidation.suggestions.length > 0 && (
                        <EmailSuggestions id="email-suggestions">
                          <div
                            id="email-suggestions-label"
                            style={{
                              marginBottom: "4px",
                              fontWeight: 600,
                            }}
                          >
                            Suggested alternatives:
                          </div>
                          {emailValidation.suggestions.map(
                            (suggestion, index) => (
                              <SuggestionItem
                                key={index}
                                id={`email-suggestion-${index}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  setFieldValue("email", suggestion);
                                  validateEmailAndPhone(
                                    suggestion,
                                    values.phone,
                                  );
                                }}
                              >
                                {suggestion}
                              </SuggestionItem>
                            ),
                          )}
                        </EmailSuggestions>
                      )}
                  </>
                )}
                {isValidating && !emailValidation && (
                  <ValidationMessage id="email-validation-loading">
                    Checking availability...
                  </ValidationMessage>
                )}
                {errors.email && touched.email && !emailValidation && (
                  <ValidationMessage role="alert">
                    {errors.email as string}
                  </ValidationMessage>
                )}
              </div>
              <Fields.Password
                name="password"
                placeholder={
                  isEditMode ? "Leave blank to keep current" : "Enter Password"
                }
                label={isEditMode ? "Password (optional)" : "Password"}
              />
            </Row>
            <Typography weight={600} size={18}>
              Address
            </Typography>

            <Row>
              <DropDown
                name="zone"
                placeholder="---Select Zone---"
                label="Zone"
                options={Array.isArray(zones) ? zones : []}
                titleAsKey="name"
                valueAsKey="id"
                styleInput="width:97.3%"
                styleOptionsWrapper="width:100%"
              />
              <Fields.Input
                name="landmark"
                placeholder="Enter Address"
                label="Landmark/Locality/Area"
              />
            </Row>
            <Row>
              <Fields.DropDown
                name="province"
                placeholder="Select Province Name"
                label="Province"
                options={provinces}
                titleAsKey="name"
                valueAsKey="slug"
                styleInput="width:97%"
                onChange={(value) => {
                  setFieldValue("province", value);
                  setSelectedProvince(value);
                }}
                hasSearch={true}
              />
              <Fields.DropDown
                name="district"
                placeholder="Select District Name"
                label="District"
                options={districts}
                titleAsKey="name"
                valueAsKey="slug"
                styleInput="width:97%"
                hasSearch={true}
                onChange={(value) => {
                  setFieldValue("district", value);
                  setSelectedDistrict(value);
                }}
                disabled={!selectedProvince}
              />
              <Fields.DropDown
                name="municiples"
                placeholder="Select Municipality Name"
                label="Municipality"
                options={municipals}
                titleAsKey="name"
                valueAsKey="slug"
                styleInput="width:97%"
                hasSearch={true}
                disabled={!selectedDistrict}
              />
            </Row>
            <Typography weight={600} size={18}>
              Identity
            </Typography>

            <Row>
              <DropDown
                name="identityType"
                placeholder="---Select Identity Type---"
                label="Select Identity Type"
                options={[
                  { id: 1, key: "Citizenship" },
                  { id: 2, key: "Driving License" },
                  { id: 3, key: "NID" },
                ]}
                titleAsKey="key"
                valueAsKey="id"
                styleInput="width:97.3%"
                styleOptionsWrapper="width:100%"
              />
              <Fields.Input
                name="identityNumber"
                label="Identity Number"
                placeholder="Enter Identity Number"
              />
            </Row>
            <Row>
              <Fields.Upload
                name="front"
                label="Front Image"
                placeholder="Upload front of your document"
              />
              <Fields.Upload
                name="back"
                label="Back Image"
                placeholder="Upload back of your document"
              />
            </Row>

            <Typography weight={600} size={18}>
              Setup
            </Typography>
            <Row>
              <Fields.GroupRadioBox
                name="deliveryType"
                label="Select Delivery Type"
                options={[
                  { id: "Freelance", key: "Freelance" },
                  { id: "Salary", key: "Salary Base" },
                ]}
                titleAsKey="key"
                valueAsKey="id"
              />
              <Fields.GroupRadioBox
                name="vehicleType"
                label="Select Vehicle Type"
                options={[
                  { id: "Bike", key: "Bike" },
                  { id: "Car", key: "Car" },
                ]}
                titleAsKey="key"
                valueAsKey="id"
              />
            </Row>
            <Row>
              {!isOtherBrand ? (
                <Fields.DropDown
                  name="vehicleBrand"
                  placeholder="Select Vehicle Brand"
                  label="Vehicle Brand"
                  options={vehicleBrand}
                  titleAsKey="label"
                  valueAsKey="value"
                  styleInput="width:97.3%"
                  styleOptionsWrapper="width:100%"
                  onChange={(value) => {
                    if (value === "Other") {
                      setIsOtherBrand(true);
                      setFieldValue("vehicleBrand", "");
                    } else {
                      setFieldValue("vehicleBrand", value);
                    }
                  }}
                />
              ) : (
                <div style={{ width: "100%" }}>
                  <Fields.Input
                    name="vehicleBrand"
                    placeholder="Enter Vehicle Brand"
                    label="Vehicle Brand"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIsOtherBrand(false);
                      setFieldValue("vehicleBrand", "");
                    }}
                    style={{
                      marginTop: "8px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      cursor: "pointer",
                      background: "#f3f4f6",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                    }}
                  >
                    ← Back to dropdown
                  </button>
                </div>
              )}
              <Fields.Input
                name="vehicleName"
                placeholder="Enter Vehicle Name/Model"
                label="Vehicle Name"
              />
              <Fields.Input
                name="vehicleNumber"
                placeholder="Enter Vehicle Number"
                label="Vehicle Number"
              />
            </Row>

            <Fields.Upload name="vehicleImage" label="Vehicle Image" />
            <Typography weight={600} size={18}>
              Upload Documents
            </Typography>
            <Row>
              <Fields.Upload
                name="frontBB"
                label="Bluebook Front"
                placeholder="Upload front of your document"
              />
              <Fields.Upload
                name="backBB"
                label="Bluebook Back"
                placeholder="Upload back of your document"
              />
            </Row>
            <Row>
              <Fields.Input
                name="number"
                label="Driving License Number"
                placeholder="Enter Driving License Number"
              />
              <DateTimePicker
                name="validity"
                label="Driving License Validity"
                placeholder="Enter Driving License Validity"
              />
            </Row>
            <Row>
              <Fields.Upload
                name="licenseFront"
                label="License Front"
                placeholder="Upload Image"
              />
              <Fields.Upload
                name="insuranceImg"
                label="Insurance Image"
                placeholder="Upload Image"
              />
            </Row>
            <ButtonWrapper>
              <Button type="reset" title="Reset" variant="reset" />
              <Button type="submit" title={isEditMode ? "Update" : "Submit"} />
            </ButtonWrapper>
          </Column>
        </Form>
      )}
    </Formik>
  );
};

export default AddDeliveryMan;
